import { useCalculateAgendaRecordMutation } from 'store/services/flow/agendaRecords';
import type { MutableRefObject } from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { buildCalculateAttributes, mapCalculateResponseToFormValues, NEW_RECORD_PREFIX } from 'utils/agendaRecord/agendaRecord.ts';
import { calculateResponseSchema } from 'utils/validations/useCalculateResponseSchema';
import type { AttributeType, UserAgendaTableDataSchemaType } from 'types';
import type { TableRef } from 'components/common/Table/Table.tsx';

interface UseAgendaTableCalculationProps {
  dataSchema: UserAgendaTableDataSchemaType;
}

export const useAgendaTableCalculation = ({ dataSchema }: UseAgendaTableCalculationProps) => {
  const { t } = useTranslation();
  const [calculateAgendaRecord] = useCalculateAgendaRecordMutation();

  const [calculatedValues, setCalculatedValues] = useState<Record<string, unknown>>();

  const handleCalculateRow = useCallback(
    async (tableRef: MutableRefObject<TableRef | null>, calculatedAgendaId: string, rowId: string, rowData: AttributeType[]) => {
      if (!rowData || Object.keys(rowData).length === 0) {
        toast.error(t('flow.modulesAndAgendas.form.item.records.noDataToCalculate'));
        return;
      }

      try {
        const attributes = buildCalculateAttributes(rowData);
        const recordId = rowId.startsWith(NEW_RECORD_PREFIX) ? null : rowId;

        const res = await calculateAgendaRecord({
          agendaId: calculatedAgendaId,
          body: { recordId, attributes },
        });

        if ('data' in res) {
          const validatedData = await calculateResponseSchema.validate(res.data);
          const updatedValues = mapCalculateResponseToFormValues(validatedData.attributes);

          if (!dataSchema || Object.keys(dataSchema).length === 0) return;

          const filteredVisibleAttributes = Object.fromEntries(Object.entries(updatedValues).filter(([key]) => key in dataSchema));

          setCalculatedValues((prev) => ({
            ...prev,
            [rowId]: {
              ...(prev?.[rowId] || {}),
              ...filteredVisibleAttributes,
            },
          }));

          tableRef.current?.updateFormDataRow(rowId, filteredVisibleAttributes);
        }
      } catch (error) {
        toast.error('Calculation failed');
      }
    },
    [calculateAgendaRecord, dataSchema, t]
  );

  const clearCalculatedValues = useCallback(() => {
    setCalculatedValues(undefined);
  }, []);

  return {
    handleCalculateRow,
    calculatedValues,
    setCalculatedValues,
    clearCalculatedValues,
  };
};
